import React, { useState, useCallback, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { View, Pressable, Image, ScrollView, Platform } from 'react-native';
import { GiftedChat } from 'react-native-gifted-chat';
import { AntDesign } from '@expo/vector-icons';
import { Text } from '@constants/Themed';
import { H3, XStack } from 'tamagui';
import ActionArray from '@components/UI/ContextActions.tsx/ActionArray';
import AnimatedLottieView from '@utils/lottieAnimation';
import * as Animatable from 'react-native-animatable';
import AnimateMarkup from '@hooks/AnimateMarkup';
import moment from 'moment';
import useGlobalUser from '@hooks/getGlobalUser';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import handleAiHelp, { ChatMessage, convertGiftedChatToHistory, sanitizeHistoryForAPI } from '@components/modals/HelpModal/utils/handleHelp';
import { uniqueId } from 'lodash';
import MarkDown from 'react-native-markdown-display';
import { rules } from '@utils/stringUtils/markDownRules';
import { LinearGradient } from 'expo-linear-gradient';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { saveChat, addMessageToChat } from '@api/api';
import { analyzeResponse, isMapperReady } from '@utils/semanticActionMapper';
import { useDispatch } from 'react-redux';

interface ChatInterfaceProps {
  chatMessages: any[];
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setChatStarted: React.Dispatch<React.SetStateAction<boolean>>;
  setFlip: React.Dispatch<React.SetStateAction<boolean>>;
  flip: boolean;
  chatOnly?: boolean;
  handleQuickReply: (reply: any) => void;
  disableInput: React.Dispatch<React.SetStateAction<boolean>>;
  isEditorConnected: boolean;
  insertContent?: (content: string) => boolean;
  setContent?: (content: string) => boolean;
  formatConversationForEditor: () => string;
  generateConversationSummary: () => string;
  onChatSaved?: () => void; // Callback to refresh chat history
}

export interface ChatInterfaceRef {
  handleQuery: (query: string) => Promise<void>;
}

const ChatInterface = forwardRef<ChatInterfaceRef, ChatInterfaceProps>(({
  chatMessages,
  setChatMessages,
  setChatStarted,
  chatOnly,
  setFlip,
  flip,
  handleQuickReply,
  disableInput,
  isEditorConnected,
  insertContent,
  setContent,
  formatConversationForEditor,
  generateConversationSummary,
  onChatSaved
}, ref) => {
  const theme = themeSelector();
  const globalUser = useGlobalUser();
  const userData = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  const [lock, setLock] = useState(false);
  const [helpSearch, setHelpSearch] = useState("");
  const [AIHelp, setAIHelp] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const { showAlert } = useAlertDialog();
  const dispatch = useDispatch();

  const hasShownDisclaimer = useRef(false);

  // Listen for chat restoration events (Redux for native, window events for web)
  const chatRestoration = useSelector((state: DefaultRootState) => state.modals.helpModal.chatRestoration);
  const shouldExpandToChat = useSelector((state: DefaultRootState) => state.modals.helpModal.shouldExpandToChat);

  // Handle Redux state changes for native
  useEffect(() => {
    if (chatRestoration && chatRestoration.messages && chatRestoration.messages.length > 0) {
      console.log('💬 [ChatInterface] restoration from Redux:', chatRestoration);
      setChatMessages(chatRestoration.messages);
      setChatStarted(true);

      if (chatRestoration.chatId) {
        setCurrentChatId(chatRestoration.chatId);
        console.log('💬 [ChatInterface] Set current chat ID:', chatRestoration.chatId);
      }

      // Show success message after a brief delay
      setTimeout(() => {
        console.log(`💬 [ChatInterface] Chat "${chatRestoration.chatTitle || 'Untitled'}" restored successfully`);
      }, 100);

      // Clear the restoration data after processing
      dispatch({ type: 'RESTORE_CHAT_HISTORY', payload: null });
    }
  }, [chatRestoration, setChatMessages, setChatStarted, dispatch]);

  useEffect(() => {
    if (shouldExpandToChat && setFlip) {
      console.log('💬 [ChatInterface] Flip to chat from Redux');
      setFlip(true);
      setChatStarted(true);

      // Reset the expand flag
      dispatch({ type: 'FLIP_HELP_TO_CHAT' });
    }
  }, [shouldExpandToChat, setFlip, setChatStarted, dispatch]);

  // Handle window events for web
  useEffect(() => {
    if (Platform.OS !== 'web') return; // Only for web

    const handleRestoreChat = (event: CustomEvent) => {
      console.log('💬 ChatInterface received restore event (web):', event.detail);
      const { messages, chatTitle, chatId } = event.detail;

      if (messages && messages.length > 0) {
        console.log('💬 Restoring chat with', messages.length, 'messages');
        setChatMessages(messages);
        setChatStarted(true);

        if (chatId) {
          setCurrentChatId(chatId);
          console.log('💬 Set current chat ID:', chatId);
        }

        // Show success message after a brief delay
        setTimeout(() => {
          console.log(`💬 Chat "${chatTitle || 'Untitled'}" restored successfully`);
        }, 100);
      } else if (event.detail?.chatId) {
      // If only chatId is provided (backward compatibility)
        setCurrentChatId(event.detail.chatId);
        console.log('💬 Set current chat ID:', event.detail.chatId);
      }
    };

    const handleFlipToChat = (event: CustomEvent) => {
      console.log('💬 ChatInterface received flip to chat event (web):', event.detail);
      if (event.detail?.shouldFlip && setFlip) {
        setFlip(true);
        setChatStarted(true);
        console.log('💬 Flipped to chat view from ChatInterface');
      }
    };

    window.addEventListener('restoreChatHistory', handleRestoreChat as EventListener);
    window.addEventListener('flipToChat', handleFlipToChat as EventListener);

    return () => {
      window.removeEventListener('restoreChatHistory', handleRestoreChat as EventListener);
      window.removeEventListener('flipToChat', handleFlipToChat as EventListener);
    };
  }, [setChatMessages, setChatStarted, setFlip]);

  // Clear chat ID when starting new conversation
  useEffect(() => {
    if (chatMessages.length === 0) {
      setCurrentChatId(null);
      console.log('💬 Cleared current chat ID - starting new conversation');
    }
  }, [chatMessages.length]);

  const _handleQuery = useCallback(async (query: string) => {
    console.log('🚀 _handleQuery called with query:', query);
    
    // Prevent rapid successive queries
    if (!flip) {
      setFlip(true);
    }
    if (!chatMessages.length) {
      console.log('🚀 No previous messages, adding disclaimer');
      await addDisclaimer();
    }
    if (!AIHelp) {
      setAIHelp(true);
    }
    if (setChatStarted) {
      setChatStarted(true);
    }

    // Extract conversation history from current chatMessages + add current query
    console.log('🔍 Current chatMessages for history extraction:', chatMessages);
    const existingHistory = convertGiftedChatToHistory(chatMessages);
    console.log('🔍 Extracted existing history:', existingHistory);
    
    const newHistory = [
      ...existingHistory,
      { role: 'user' as const, content: query.trim(), timestamp: new Date() }
    ];
    console.log('🔍 Full history including current query:', JSON.stringify(newHistory, null, 2));

    // Add user message with optimized user object to UI
    const userMessage = {
      _id: Math.random().toString(36).substring(7),
      text: query,
      createdAt: new Date(),
      user: { _id: globalUser, avatar: userData.avatar?.img }
    };
    setChatMessages(previousMessages => GiftedChat.append(previousMessages, [userMessage]));

    disableInput(true);

    try {
      // Use the conversation history from state (always current)
      const fullHistory = [...newHistory];

      // Reduced logging for better performance - only log in development
      if (__DEV__) {
        console.log('🔍 Full history to send to AI:', JSON.stringify(fullHistory, null, 2));
      }

      // Enhance the prompt with context when editor is available
      let enhancedQuery = query;
      if (isEditorConnected) {
        const currentContent = formatConversationForEditor ? formatConversationForEditor().substring(0, 200) : '';
        if (currentContent && currentContent.trim().length > 0) {
          enhancedQuery += `\n\nContext: I'm working on content that currently contains: "${currentContent}${currentContent.length > 200 ? '...' : ''}"`;
        } else {
          enhancedQuery += "\n\nNote: I'm working in a content editor and would like the response formatted for easy insertion.";
        }
      }

      // Pass conversation history to AI for context-aware responses
      if (__DEV__) {
        console.log('📧 Sending to AI - Full History:', fullHistory);
        console.log('📧 Sending to AI - History Length:', fullHistory.length);
        console.log('📧 Sending to AI - Enhanced Query:', enhancedQuery);
      }
      
      const response = await handleAiHelp(isEditorConnected, enhancedQuery, fullHistory);
      
      if (__DEV__) {
        console.log('AI Request - Enhanced Query:', enhancedQuery);
        console.log('AI Request - History Length:', fullHistory.length);
        console.log('AI Request - History:', fullHistory);
        console.log('AI Response:', response);
        console.log('AI Response Type:', typeof response);
        console.log('AI Response.response:', response.response);
        console.log('AI Response.response Type:', typeof response.response);
      }

      if (!response || (!response.response && response.response !== '')) {
        throw new Error('Network response was not ok.');
      }

      // Parse the entire JSON response
      const data = response;
      const uid = uniqueId();

      // Create a message with the ID
      setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
        _id: uid,
        text: '',
        createdAt: new Date(),
        system: true,
        received: true,
        user: { system: true }
      }]));

      // Handle parsed data (assuming it's an array of responses)
      if (Array.isArray(data)) {
        data.forEach(item => {
          setChatMessages(prevMessages => {
            const updatedMessages = prevMessages.map(message => {
              if (message._id === uid) {
                return { ...message, text: message.text + item.response, user: { system: true }, system: true };
              }
              return message;
            });

            // Add the message if it doesn't exist
            if (!updatedMessages.some(message => message._id === uid)) {
              updatedMessages.push({
                _id: uid,
                text: item.response,
                createdAt: new Date(),
                user: { system: true },
                system: true
              });
            }

            return updatedMessages;
          });
        });
      } else {
        const stripThink = data.response.replace(/<think>/g, '').replace(/<\/think>/g, '');
        
        // No need to maintain separate state - AI response will be added to chatMessages
        console.log('🔍 AI response will be added to chatMessages automatically');
        
        // Analyze AI response for actionable intents
        if (isMapperReady()) {
          analyzeResponse(stripThink).then(matches => {
            if (matches.length > 0) {
              console.log('🎯 Detected actions in AI response:', matches);
              // Dispatch event to HelpSidePanel to show action chips
              const event = new CustomEvent('aiActionsDetected', {
                detail: { actions: matches }
              });
              window.dispatchEvent(event);
            }
          }).catch(error => {
            console.error('❌ Failed to analyze AI response:', error);
          });
        }

        // Handle a single response object
        setChatMessages(prevMessages => {
          const updatedMessages = prevMessages.map(message => {
            if (message._id === uid) {
              return {
                ...message,
                text: message.text.toString() + stripThink,
                // Add editor actions if editor context is available
                ...(isEditorConnected && insertContent && {
                  quickReplies: {
                    type: 'radio',
                    keepIt: true,
                    values: [

                      {
                        title: '🔄 Replace All Content',
                        value: 'replace_editor',
                        messageId: Math.random().toString(36).substring(7),
                      },
                      {
                        title: '📋 Copy to Clipboard',
                        value: 'copy_clipboard',
                        messageId: Math.random().toString(36).substring(7),
                      }
                    ]
                  }
                })
              };
            }
            return message;
          });

          if (!updatedMessages.some(message => message._id === uid)) {
            updatedMessages.push({
              _id: uid,
              text: stripThink,
              createdAt: new Date(),
              system: true,
              received: true,
              user: { system: true },
              // Add editor actions if editor context is available
              ...(isEditorConnected && insertContent && {
                quickReplies: {
                  type: 'radio',
                  keepIt: true,
                  values: [
                    {
                      title: '📝 Insert into Editor',
                      value: 'insert_editor',
                      messageId: Math.random().toString(36).substring(7),
                    },
                    {
                      title: '🔄 Replace All Content',
                      value: 'replace_editor',
                      messageId: Math.random().toString(36).substring(7),
                    },
                    {
                      title: '📋 Copy to Clipboard',
                      value: 'copy_clipboard',
                      messageId: Math.random().toString(36).substring(7),
                    }
                  ]
                }
              })
            });
          }

          return updatedMessages;
        });

        // Re-enable input immediately since there's no animation
        disableInput(false);
      }

    } catch (err) {
      console.error('AI Query Error:', err);
      setChatMessages(previousMessages =>
        GiftedChat.append(previousMessages, [
          {
            _id: Math.random().toString(36).substring(7),
            text: 'Sorry, I am not able to answer that right now. Please try again later.',
            createdAt: new Date(),
            system: true,
            received: true
          }
        ])
      );
    } finally {
      disableInput(false);
    }
  }, [flip, AIHelp, globalUser, userData.avatar?.img, isEditorConnected, insertContent, formatConversationForEditor, setChatMessages, setFlip, disableInput, setChatStarted]);

  const addDisclaimer = () => {
    return new Promise<void>((resolve, reject) => {
      setChatMessages(previous => GiftedChat.append(previous, [{
        _id: Math.random().toString(36).substring(7),
        text: "AI generated responses are not always accurate, please use caution when using this feature.",
        isDisclaimer: true,
        system: true,
        createdAt: new Date(),
        user: { _id: "AI" }
      }]))
      resolve();
    })
  }

  // Clear conversation history when starting new chat - no separate state needed
  const clearConversationHistory = useCallback(() => {
    // History is extracted from chatMessages, so clearing chatMessages clears history
    console.log('🧹 Conversation history will be cleared when chatMessages are cleared');
  }, []);

  // Helper function to check if chat can be saved
  const canSaveChat = useCallback(() => {
    if (!chatMessages.length) return false;

    const userMessages = chatMessages.filter(msg => msg.user && msg.user._id !== 'AI' && !msg.system);
    const assistantMessages = chatMessages.filter(msg => msg.user && msg.user._id === 'AI' && !msg.system);

    // Must have user messages
    if (userMessages.length === 0) return false;

    // Must have at least one meaningful exchange
    const meaningfulExchanges = Math.min(userMessages.length, assistantMessages.length);
    if (meaningfulExchanges < 1) return false;

    return true;
  }, [chatMessages]);

  // Function to save chat conversation
  const saveChatConversation = useCallback(async () => {
    try {
      if (!chatMessages.length) {
        console.log('📝 Cannot save chat: no messages to save');
        return;
      }

      // Check if there are meaningful user interactions (not just system messages)
      const userMessages = chatMessages.filter(msg => msg.user && msg.user._id !== 'AI' && !msg.system);
      const assistantMessages = chatMessages.filter(msg => msg.user && msg.user._id === 'AI' && !msg.system);

      console.log('📝 Chat validation:', {
        totalMessages: chatMessages.length,
        userMessages: userMessages.length,
        assistantMessages: assistantMessages.length,
        hasUserInteraction: userMessages.length > 0,
        hasAssistantResponse: assistantMessages.length > 0
      });

      // Don't save if there are no user messages or no meaningful conversation
      if (userMessages.length === 0) {
        console.log('📝 Cannot save chat: no user messages found');
        showAlert('Cannot save chat: No user messages to save. Start a conversation first.');
        return;
      }

      // Don't save if there's only one user message and no assistant response
      if (userMessages.length === 1 && assistantMessages.length === 0) {
        console.log('📝 Cannot save chat: only one user message without response');
        showAlert('Cannot save chat: Wait for AI response before saving.');
        return;
      }

      // Don't save if conversation is too short (less than 2 meaningful exchanges)
      const meaningfulExchanges = Math.min(userMessages.length, assistantMessages.length);
      if (meaningfulExchanges < 1) {
        console.log('📝 Cannot save chat: insufficient conversation content');
        showAlert('Cannot save chat: Need at least one question and answer exchange.');
        return;
      }

      const userId = globalUser?._id || globalUser?.id || globalUser?.userId;
      const companyId = userData?.companyId || userData?.company?._id || userData?.company?.id;

      // Convert chat messages to the format expected by the API
      const messages = chatMessages.map(msg => {
        const role = (msg.user && msg.user._id === 'AI') ? 'assistant' : 'user';
        const content = msg.text || msg.content || '';

        console.log('📝 Converting message:', {
          originalUser: msg.user,
          originalText: msg.text,
          originalContent: msg.content,
          role,
          content,
          createdAt: msg.createdAt
        });

        return {
          role: role as 'user' | 'assistant' | 'system',
          content: content
        };
      });

      // Calculate session duration (from first message to last)
      const sessionDuration = chatMessages.length > 1
        ? new Date(chatMessages[0].createdAt).getTime() - new Date(chatMessages[chatMessages.length - 1].createdAt).getTime()
        : 0;

      const chatData = {
        userId: userId,
        companyId: companyId,
        messages,
        metadata: {
          editorConnected: isEditorConnected,
          sessionDuration: Math.abs(sessionDuration),
          aiModel: 'gpt-3.5-turbo',
          messageCount: chatMessages.length,
          chatInterface: 'help-modal'
        }
      };

      console.log('💾 Saving chat conversation:', chatData);
      console.log('💾 Current chat ID:', currentChatId);

      let result;

      if (currentChatId) {
        // Update existing chat - add new messages to existing chat
        console.log('💾 Updating existing chat:', currentChatId);

        // Get only new messages (messages added after restoration)
        const newMessages = messages.filter((msg, index) => {
          // Check if this is a new message by looking for messages without 'restored-' prefix
          const originalMessage = chatMessages[index];
          return originalMessage && !originalMessage._id?.startsWith('restored-');
        });

        if (newMessages.length > 0) {
          console.log('💾 Adding', newMessages.length, 'new messages to existing chat');

          for (const message of newMessages) {
            const addResult = await addMessageToChat(currentChatId, {
              role: message.role,
              content: message.content
            }, userId);

            if (!addResult?.success) {
              console.error('❌ Failed to add message to chat:', addResult?.message);
              showAlert('Save Failed: Could not update your conversation. Please try again.');
              return;
            }
          }

          result = { success: true, data: { chatId: currentChatId, title: 'Updated Chat' } };
        } else {
          console.log('💾 No new messages to add - chat is already up to date');
          result = { success: true, data: { chatId: currentChatId, title: 'Chat Up to Date' } };
        }
      } else {
        // Create new chat
        console.log('💾 Creating new chat');
        result = await saveChat(chatData);
      }

      console.log('💾 Save result:', result);

      if (result?.success) {
        console.log('✅ Chat saved successfully:', result.data);
        const actionText = currentChatId ? 'updated' : 'saved';
        showAlert(`Chat ${actionText}: Your conversation has been ${actionText} as "${result.data?.title || 'Chat'}"`);

        // Set the chat ID if this was a new chat
        if (!currentChatId && result.data?.chatId) {
          setCurrentChatId(result.data.chatId);
          console.log('💾 Set current chat ID to:', result.data.chatId);
        }

        // Trigger real-time update of chat history via callback
        if (onChatSaved) {
          console.log('🔄 Triggering chat history refresh via callback');
          onChatSaved();
        }

        // Also dispatch event for cross-component communication (EasyModeScreen, etc.)
        if (typeof window !== 'undefined') {
          const refreshEvent = new CustomEvent('chatSaved', {
            detail: { chatId: result.data?.chatId, source: 'help-modal' }
          });
          window.dispatchEvent(refreshEvent);
          console.log('🔄 Dispatched chatSaved event to refresh chat history');
        }
      } else {
        console.error('❌ Failed to save chat:', result?.message || 'Unknown error');
        showAlert('Save Failed: Could not save your conversation. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error saving chat:', error);

      // Log more detailed error information
      if (error.response) {
        console.error('❌ Error response data:', error.response.data);
        console.error('❌ Error response status:', error.response.status);
        console.error('❌ Error response headers:', error.response.headers);
      } else if (error.request) {
        console.error('❌ Error request:', error.request);
      } else {
        console.error('❌ Error message:', error.message);
      }

      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while saving your conversation.';
      showAlert(`Save Failed: ${errorMessage}`);
    }
  }, [chatMessages, globalUser, userData, isEditorConnected, showAlert, onChatSaved, currentChatId]);

  // Expose the handleQuery function via ref
  useImperativeHandle(ref, () => ({
    handleQuery: _handleQuery
  }), [_handleQuery]);

  // Memoize user object for GiftedChat to prevent re-renders
  const giftedChatUser = useMemo(() => ({
    _id: globalUser,
    avatar: userData.avatar?.img
  }), [globalUser, userData.avatar?.img]);

  // Memoize avatar renderer to prevent recreation
  const renderAvatar = useCallback((props) => {
    const avatarUri = typeof props.currentMessage.user.avatar === 'string' ? props.currentMessage.user.avatar : undefined;
    return (
      <Image
        source={{ uri: avatarUri }}
        style={{ height: 25, width: 25, borderRadius: 25 }}
      />
    )
  }, []);

  // Memoize system message component styles to prevent recreation
  const systemMessageStyles = useMemo(() => ({
    container: {
      marginHorizontal: 20,
      borderRadius: 10,
      padding: 12,
      paddingHorizontal: 12,
      backgroundColor: theme ? '#f3f4f6' : '#333333',
      maxWidth: "85%" as const,
      // Remove minHeight constraint to allow natural content sizing
    },
    animation: {
      left: 10,
      width: 25,
      alignSelf: "flex-end" as const,
      justifyContent: "flex-end" as const
    }
  }), [theme]);

  return (
    <View style={{
      flex: 1,
      backgroundColor: theme ? '#ffffff' : '#1a1a1a',
      minHeight: 0, // Important for proper flex behavior
    }}>
      {/* ChatGPT-like Header with Action Buttons */}
      <View style={{
        backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(26, 26, 26, 0.95)',
        borderBottomWidth: 1,
        borderBottomColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
        paddingHorizontal: 16,
        paddingVertical: 12,
        zIndex: 10,
      }}>
        <XStack maxWidth="100%" alignSelf="center" justifyContent="space-between" alignItems="center">

          <XStack gap={8}>
            <Pressable
              style={{
                backgroundColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 16,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
              }}
              onPress={async () => {
                // Save chat conversation before going back
                if (chatMessages.length > 0) {
                  await saveChatConversation();
                }
              if (setChatStarted) setChatStarted(false);
              setFlip(!flip);
              }}
            >
              <AntDesign name="arrow-left" size={14} color={theme ? "#374151" : "#e5e7eb"} />
              <Text style={{
                fontSize: 12,
                color: theme ? "#374151" : "#e5e7eb",
                fontWeight: '500',
              }}>
                Back
              </Text>
            </Pressable>
            <Pressable
              style={{
                backgroundColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 16,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
              }}
              onPress={() => {
              setChatMessages([]);
                clearConversationHistory();
              setAIHelp(false);
              if (setChatStarted) setChatStarted(false);
              }}
            >
              <AntDesign name="plus" size={14} color={theme ? "#374151" : "#e5e7eb"} />
              <Text style={{
                fontSize: 12,
                color: theme ? "#374151" : "#e5e7eb",
                fontWeight: '500',
              }}>
                New Chat
              </Text>
            </Pressable>


            {/* Save Chat Button */}
            {chatMessages.length > 0 && (
              <Pressable
                style={{
                  backgroundColor: canSaveChat()
                    ? (theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)')
                    : (theme ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.05)'),
                  paddingHorizontal: 12,
                  paddingVertical: 6,
                  borderRadius: 16,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  opacity: canSaveChat() ? 1 : 0.5,
                }}
                onPress={canSaveChat() ? saveChatConversation : undefined}
                disabled={!canSaveChat()}
              >
                <AntDesign
                  name="save"
                  size={12}
                  color={canSaveChat()
                    ? (theme ? "#3b82f6" : "#60a5fa")
                    : (theme ? "#9ca3af" : "#6b7280")
                  } 
                />
                <Text style={{
                  fontSize: 10,
                  color: canSaveChat()
                    ? (theme ? "#3b82f6" : "#60a5fa")
                    : (theme ? "#9ca3af" : "#6b7280"),
                  fontWeight: '600',
                }}>
                  {canSaveChat() ? 'Save Chat' : 'Need Q&A'}
                </Text>
              </Pressable>
            )}

      </XStack>
        </XStack>
      </View>

      {/* ChatGPT-like Chat Messages */}
      <View style={{
        flex: 1,
        minHeight: 0,
        paddingBottom: 140, // Add padding for floating input and quick actions
        paddingTop: 16,
      }}>
      <GiftedChat
        messages={chatMessages}
        key={"aiHelpChat"}
        showUserAvatar
        minInputToolbarHeight={0}
        renderInputToolbar={() => (<View />)}
        onQuickReply={handleQuickReply}
          listViewProps={{}}
          renderMessage={props => {
            const isUser = props.currentMessage.user && props.currentMessage.user._id !== "AI" && !props.currentMessage.system;

            return (
              <View style={{
                marginVertical: 4,
                marginHorizontal: 16,
                flexDirection: 'row',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                flexShrink: 0, // Prevent message containers from shrinking
              }}>
                <View style={{
                  maxWidth: '80%',
                  minWidth: '20%', // Ensure minimum width for readability
                  backgroundColor: isUser
                    ? (theme ? '#3b82f6' : '#1e40af')
                    : (theme ? '#f8fafc' : '#374151'),
                  borderRadius: 18,
                  paddingHorizontal: 16,
                  paddingVertical: 12,
                  borderWidth: 1,
                  borderColor: theme
                    ? (isUser ? 'transparent' : 'rgba(0, 0, 0, 0.08)')
                    : (isUser ? 'transparent' : 'rgba(255, 255, 255, 0.1)'),
                  shadowColor: theme ? '#000' : '#000',
                  shadowOffset: { width: 0, height: 1 },
                  shadowOpacity: theme ? 0.05 : 0.2,
                  shadowRadius: 2,
                  elevation: 1,
                  // Remove any height constraints to allow content to expand naturally
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}>
                  {/* Render markdown content for AI messages, plain text for user messages */}
                  {!isUser ? (
                    <MarkDown
                      style={{
                        body: {
                          fontSize: 15,
                          lineHeight: 22,
                          color: theme ? '#374151' : '#e5e7eb',
                          margin: 0,
                          padding: 0,
                        },
                        paragraph: {
                          marginTop: 0,
                          marginBottom: 8,
                          fontSize: 15,
                          lineHeight: 22,
                        },
                        heading1: {
                          fontSize: 18,
                          fontWeight: 'bold',
                          marginTop: 8,
                          marginBottom: 4,
                          color: theme ? '#1f2937' : '#f3f4f6',
                        },
                        heading2: {
                          fontSize: 16,
                          fontWeight: 'bold',
                          marginTop: 6,
                          marginBottom: 4,
                          color: theme ? '#1f2937' : '#f3f4f6',
                        },
                        heading3: {
                          fontSize: 15,
                          fontWeight: 'bold',
                          marginTop: 4,
                          marginBottom: 4,
                          color: theme ? '#1f2937' : '#f3f4f6',
                        },
                        code_inline: {
                          backgroundColor: theme ? '#f3f4f6' : '#374151',
                          color: theme ? '#e11d48' : '#fbbf24',
                          paddingHorizontal: 4,
                          paddingVertical: 2,
                          borderRadius: 4,
                          fontSize: 14,
                          fontFamily: 'monospace',
                        },
                        code_block: {
                          backgroundColor: theme ? '#f3f4f6' : '#374151',
                          color: theme ? '#1f2937' : '#f3f4f6',
                          padding: 12,
                          borderRadius: 8,
                          fontSize: 14,
                          fontFamily: 'monospace',
                          marginVertical: 8,
                        },
                        blockquote: {
                          backgroundColor: theme ? '#f8fafc' : '#1e293b',
                          borderLeftWidth: 4,
                          borderLeftColor: theme ? '#3b82f6' : '#60a5fa',
                          paddingLeft: 12,
                          paddingVertical: 8,
                          marginVertical: 8,
                          fontStyle: 'italic',
                        },
                        list_item: {
                          fontSize: 15,
                          lineHeight: 22,
                          marginBottom: 4,
                        },
                        ordered_list: {
                          fontSize: 15,
                          lineHeight: 22,
                        },
                        bullet_list: {
                          fontSize: 15,
                          lineHeight: 22,
                        },
                        link: {
                          color: theme ? '#3b82f6' : '#60a5fa',
                          textDecorationLine: 'underline',
                        },
                        strong: {
                          fontWeight: 'bold',
                        },
                        em: {
                          fontStyle: 'italic',
                        },
                      }}
                      rules={rules}
                    >
                      {props.currentMessage.text}
                    </MarkDown>
                  ) : (
                    <Text style={{
                      fontSize: 15,
                      lineHeight: 22,
                      color: 'white',
                    }}>
                      {props.currentMessage.text}
                    </Text>
                  )}

                  {/* Quick Reply Buttons for AI messages */}
                  {!isUser && props.currentMessage.quickReplies && (
                    <View style={{ marginTop: 12, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {props.currentMessage.quickReplies.values.map((quickReply, index) => (
                        <Pressable
                          key={index}
                          style={{
                            backgroundColor: '#3b82f6',
                            paddingHorizontal: 10,
                            paddingVertical: 6,
                            borderRadius: 12,
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 4,
                          }}
                          onPress={() => handleQuickReply({
                            value: quickReply.value,
                            messageId: quickReply.messageId
                          })}
                        >
                          <Text style={{ fontSize: 12, color: 'white', fontWeight: '500' }}>
                            {quickReply.title}
                          </Text>
                        </Pressable>
                      ))}
                    </View>
                  )}

                  {/* Editor Actions for AI messages */}
                  {!isUser && isEditorConnected && insertContent && props.currentMessage.received && (
                    <View style={{ marginTop: 12, flexDirection: 'row', gap: 6 }}>
                      <Pressable
                        style={{
                          backgroundColor: '#10b981',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 8,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onPress={() => {
                          const fullConversation = formatConversationForEditor();
                          if (insertContent && insertContent(fullConversation)) {
                            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                              _id: Math.random().toString(36).substring(7),
                              text: '✅ Full conversation sent to editor successfully!',
                              createdAt: new Date(),
                              system: true,
                              received: true
                            }]));
                          }
                        }}
                      >
                        <AntDesign name="edit" size={10} color="white" />
                        <Text style={{ fontSize: 10, color: 'white', fontWeight: '600' }}>
                          Full Chat
                        </Text>
                      </Pressable>

                      <Pressable
                        style={{
                          backgroundColor: '#3b82f6',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 8,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onPress={() => {
                          const conversationSummary = generateConversationSummary();
                          if (insertContent && insertContent(conversationSummary)) {
                            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                              _id: Math.random().toString(36).substring(7),
                              text: '✅ Conversation summary sent to editor successfully!',
                              createdAt: new Date(),
                              system: true,
                              received: true
                            }]));
                          }
                        }}
                      >
                        <AntDesign name="copy" size={10} color="white" />
                        <Text style={{ fontSize: 10, color: 'white', fontWeight: '600' }}>
                          Summary
                        </Text>
                      </Pressable>
                    </View>
                  )}

                  <Text style={{
                    fontSize: 11,
                    color: isUser ? 'rgba(255, 255, 255, 0.7)' : (theme ? '#9ca3af' : '#6b7280'),
                    marginTop: 4,
                    alignSelf: 'flex-end',
                  }}>
                    {moment(props.currentMessage.createdAt).format("HH:mm")}
                  </Text>
                </View>
              </View>
            );
        }}
        renderSystemMessage={props => {
          return (
            <View
              style={{
                flexDirection: "row",
                marginBottom: 16,
                marginHorizontal: 16,
                alignItems: "flex-start",
                flexShrink: 0, // Prevent system message containers from shrinking
              }}
            >
              <View style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: theme ? '#f3f4f6' : '#374151',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: 12,
                marginTop: 4,
              }}>
              <AnimatedLottieView
                source={require("@assets/animations/automation_robot.json")}
                autoPlay
                loop
                  style={{ width: 20, height: 20 }}
              />
              </View>
              <View
                style={[
                  {
                    flex: 1,
                    backgroundColor: theme ? '#f8fafc' : '#374151',
                    borderRadius: 18,
                    paddingHorizontal: 16,
                    paddingVertical: 12,
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)',
                    shadowColor: theme ? '#000' : '#000',
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: theme ? 0.05 : 0.2,
                    shadowRadius: 2,
                    elevation: 1,
                    // Allow content to expand naturally without height constraints
                    flexShrink: 0,
                    alignSelf: 'flex-start',
                    maxWidth: '100%',
                  }
                ]}
              >
                <MarkDown
                  style={{
                    body: {
                      fontSize: 15,
                      lineHeight: 22,
                      color: theme ? '#374151' : '#e5e7eb',
                      margin: 0,
                      padding: 0,
                    },
                    paragraph: {
                      marginTop: 0,
                      marginBottom: 8,
                      fontSize: 15,
                      lineHeight: 22,
                    },
                    heading1: {
                      fontSize: 18,
                      fontWeight: 'bold',
                      marginTop: 8,
                      marginBottom: 4,
                      color: theme ? '#1f2937' : '#f3f4f6',
                    },
                    heading2: {
                      fontSize: 16,
                      fontWeight: 'bold',
                      marginTop: 6,
                      marginBottom: 4,
                      color: theme ? '#1f2937' : '#f3f4f6',
                    },
                    heading3: {
                      fontSize: 15,
                      fontWeight: 'bold',
                      marginTop: 4,
                      marginBottom: 4,
                      color: theme ? '#1f2937' : '#f3f4f6',
                    },
                    code_inline: {
                      backgroundColor: theme ? '#f3f4f6' : '#374151',
                      color: theme ? '#e11d48' : '#fbbf24',
                      paddingHorizontal: 4,
                      paddingVertical: 2,
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: 'monospace',
                    },
                    code_block: {
                      backgroundColor: theme ? '#f3f4f6' : '#374151',
                      color: theme ? '#1f2937' : '#f3f4f6',
                      padding: 12,
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'monospace',
                      marginVertical: 8,
                    },
                    blockquote: {
                      backgroundColor: theme ? '#f8fafc' : '#1e293b',
                      borderLeftWidth: 4,
                      borderLeftColor: theme ? '#3b82f6' : '#60a5fa',
                      paddingLeft: 12,
                      paddingVertical: 8,
                      marginVertical: 8,
                      fontStyle: 'italic',
                    },
                    list_item: {
                      fontSize: 15,
                      lineHeight: 22,
                      marginBottom: 4,
                    },
                    ordered_list: {
                      fontSize: 15,
                      lineHeight: 22,
                    },
                    bullet_list: {
                      fontSize: 15,
                      lineHeight: 22,
                    },
                    link: {
                      color: theme ? '#3b82f6' : '#60a5fa',
                      textDecorationLine: 'underline',
                    },
                    strong: {
                      fontWeight: 'bold',
                    },
                    em: {
                      fontStyle: 'italic',
                    },
                  }}
                  rules={rules}
                >
                  {props.currentMessage.text}
                </MarkDown>

                {/* Quick Reply Buttons for Editor Actions */}
                {props.currentMessage.quickReplies && (
                  <View style={{ marginTop: 12, flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                    {props.currentMessage.quickReplies.values.map((quickReply, index) => (
                      <Pressable
                        key={index}
                        style={{
                          backgroundColor: '#3b82f6',
                          paddingHorizontal: 10,
                          paddingVertical: 6,
                          borderRadius: 12,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onPress={() => handleQuickReply({
                          value: quickReply.value,
                          messageId: quickReply.messageId
                        })}
                      >
                        <Text style={{ fontSize: 12, color: 'white', fontWeight: '500' }}>
                          {quickReply.title}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                )}

                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  <Text style={{
                    fontSize: 11,
                    color: theme ? '#9ca3af' : '#6b7280',
                  }}>
                    {moment(props.currentMessage.createdAt).format("HH:mm")}
                  </Text>

                  {/* Send to Editor buttons - only show for AI responses when editor is connected and insertContent function is available */}
                  {!chatOnly && isEditorConnected && insertContent && props.currentMessage.system && props.currentMessage.received && (
                    <View style={{ flexDirection: 'row', gap: 6 }}>
                      {/* Send Full Conversation Button */}
                      <Pressable
                        style={{
                          backgroundColor: '#10b981',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 8,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onPress={() => {
                          const fullConversation = formatConversationForEditor();
                          if (insertContent && insertContent(fullConversation)) {
                            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                              _id: Math.random().toString(36).substring(7),
                              text: '✅ Full conversation sent to editor successfully!',
                              createdAt: new Date(),
                              system: true,
                              received: true
                            }]));
                          } else {
                            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                              _id: Math.random().toString(36).substring(7),
                              text: '❌ Failed to send conversation to editor. Please try again.',
                              createdAt: new Date(),
                              system: true,
                              received: true
                            }]));
                          }
                        }}
                      >
                        <AntDesign name="edit" size={10} color="white" />
                        <Text style={{ fontSize: 10, color: 'white', fontWeight: '600' }}>
                          Full Chat
                        </Text>
                      </Pressable>

                      {/* Send Summary Button */}
                      <Pressable
                        style={{
                          backgroundColor: '#3b82f6',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 8,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onPress={() => {
                          const conversationSummary = generateConversationSummary();
                          if (insertContent && insertContent(conversationSummary)) {
                            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                              _id: Math.random().toString(36).substring(7),
                              text: '✅ Conversation summary sent to editor successfully!',
                              createdAt: new Date(),
                              system: true,
                              received: true
                            }]));
                          } else {
                            setChatMessages(prevMessages => GiftedChat.append(prevMessages, [{
                              _id: Math.random().toString(36).substring(7),
                              text: '❌ Failed to send summary to editor. Please try again.',
                              createdAt: new Date(),
                              system: true,
                              received: true
                            }]));
                          }
                        }}
                      >
                        <AntDesign name="copy" size={10} color="white" />
                        <Text style={{ fontSize: 10, color: 'white', fontWeight: '600' }}>
                          Summary
                        </Text>
                      </Pressable>
                    </View>
                  )}
                </View>
              </View>
            </View>
          );
        }}
        user={giftedChatUser}
          renderAvatar={() => null} // Disable default avatar rendering since we're using custom message rendering
      />
      </View>
    </View>
  );
});

export default ChatInterface;
